import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useGetCountryByCodeQuery, useSearchCountriesQuery } from '@massilia/queries';
import { AutoComplete, useDebounce } from '@massilia/ui';
import { useStore } from '@tanstack/react-form';
import { MapPin } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFieldContext } from '../hooks/use-app-form.js';
import { DEFAULT_OPTION } from '../schemas/estimate/base-schemas.js';
import { FieldDisplayValue } from './field-display-value.js';
import { FieldError } from './field-error.js';
export function CountryAutocompleteField({ autoFocus, emptyMessage = 'Aucun pays trouvé', label, onDisplayClick, placeholder = 'Pays de délivrance', renderType = 'edit', }) {
    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText, 200);
    const { data, isLoading } = useSearchCountriesQuery({
        search: debouncedSearchText,
    });
    const options = useMemo(() => {
        if (!data?.countries)
            return [];
        return data.countries.map((c) => ({
            label: c.frShortName,
            value: c.alpha3Code,
        }));
    }, [data]);
    const field = useFieldContext();
    const errors = useStore(field.store, (state) => state.meta.errors);
    // When the form was seeded with `{ value: 'FRA', label: '' }` (e.g. from a saved
    // alpha-3 code without a resolved name), look up the country by code so we can
    // render the proper label without mutating form state.
    const fallbackCode = field.state.value?.value && !field.state.value?.label ? field.state.value.value : null;
    const fallbackQuery = useGetCountryByCodeQuery({ code: fallbackCode });
    const fallbackCountry = fallbackQuery.data?.country ?? null;
    const selectedOption = useMemo(() => {
        const currentValue = field.state.value?.value;
        if (!currentValue)
            return;
        // First try to find in provided options
        const foundOption = options.find((opt) => opt.value === currentValue);
        if (foundOption)
            return foundOption;
        // Fallback to the provided option if it has a label (initial value case)
        if (field.state.value?.label) {
            return field.state.value;
        }
        // Last resort: resolve the alpha-3 code into a display label via the lookup query
        if (fallbackCountry && fallbackCountry.alpha3Code === currentValue.toUpperCase()) {
            return { label: fallbackCountry.frShortName, value: currentValue };
        }
        return;
    }, [options, field.state.value, fallbackCountry]);
    if (renderType === 'display') {
        // Le champ peut avoir été initialisé avec un simple code alpha-3 sans label
        // (ex. `{ value: 'FRA', label: '' }`) : on résout le nom via le lookup pour
        // ne pas afficher un pays vide.
        const displayLabel = field.state.value?.label ||
            (fallbackCountry && fallbackCountry.alpha3Code === field.state.value?.value?.toUpperCase()
                ? fallbackCountry.frShortName
                : '');
        return _jsx(FieldDisplayValue, { label: label, onClick: onDisplayClick, value: displayLabel });
    }
    return (_jsxs("div", { className: "relative flex flex-col gap-2", children: [label && _jsx("label", { className: "text-sm font-medium", children: label }), _jsx(AutoComplete, { autoFocus: autoFocus, emptyMessage: emptyMessage, icon: _jsx(MapPin, { className: "h-4 w-4 text-gray-light-700" }), isLoading: isLoading, onBlurCallBack: () => {
                    field.handleBlur();
                }, onInputChange: (text) => {
                    setSearchText(text);
                }, onValueChange: (opt) => {
                    if (opt) {
                        field.handleChange(opt);
                    }
                    else {
                        field.handleChange(DEFAULT_OPTION);
                    }
                }, options: options, placeholder: placeholder, shouldFilter: false, value: selectedOption }), _jsx(FieldError, { errors: errors })] }));
}
